import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Snackbar, Alert } from '@mui/material';
import DashboardHeader from '../layout/DashboardHeader';
import PicassoControls from '../layout/PicassoControls';
import VideoGallery from '../VideoGallery';
import TranscriptionCompleteModal from '../modals/TranscriptionCompleteModal';
import { useVideoGeneration } from '@/hooks/dashboard/useVideoGeneration';
import { VIDEO_MODELS, IMAGE_MODELS, TRANSCRIPTION_MODELS, AIModel, supportsMultipleImages } from '@/constants/models';
import { useUserVideosAdapter, useUserImagesAdapter } from '@/hooks/querry_hooks/admin/adapters';
import { useWebSocketSync } from '@/hooks/querry_hooks/common/useWebSocketSync';
import { useDeleteVideo } from '@/hooks/querry_hooks/dashboard/useVideosQuery';
import { useDeleteImage } from '@/hooks/querry_hooks/dashboard/useImagesQuery';
import { useLoading } from '../../loading/LoadingContext';
import { LAYOUT_CLASSES } from '@/components/ui/styles/componentClasses';
import { useWebSocket } from '@/utils/contexts/WebSocketContext';
import { Transcription, useTranscription } from '@/hooks/dashboard/useTranscription';
import { useDragAndDrop } from '@/hooks/common/useDragAndDrop';
import { UserVideo, getVideoLastFrame } from '@/api/videoApi';
import { UserImage } from '@/api/imageApi';

type TabType = 'Videos' | 'Images' | 'Transcripts';

interface DashboardProps {
  userEmail: string | null;
  isAdmin: boolean;
}

const PicassoDashboard: React.FC<DashboardProps> = ({ userEmail, isAdmin }) => {
  const [searchParams] = useSearchParams();
  const videoParam = searchParams.get('video');

  // When a shared link is opened, hold off gallery fetching until the modal
  // has opened (fetchSharedVideo resolves). This ensures the popup appears
  // first and the gallery loads silently in the background afterwards.
  const [galleryEnabled, setGalleryEnabled] = useState(!videoParam);

  // WebSocket sync for React Query cache invalidation
  useWebSocketSync(userEmail, isAdmin);

  // Delete mutations with optimistic updates
  const deleteVideoMutation = useDeleteVideo();
  const deleteImageMutation = useDeleteImage();

  const requestLimit = 12;
  const [serverFilters, setServerFilters] = useState<{
    status?: string;
    sort_by?: string;
  }>({
    status: 'all',  // Default to show all videos
    sort_by: 'recent'  // Default to recent sort
  });

  const [imageFilters, setImageFilters] = useState<{
    status?: string;
    sort_by?: string;
  }>({
    status: 'all',  // Default to show all images
    sort_by: 'recent'  // Default to recent sort
  });
  const [pageValue, setPageValue] = useState(() => {
    const savedPage = localStorage.getItem('selectedPage');
    if (savedPage === 'Transcripts') return 'Transcripts';
    if (savedPage === 'Images') return 'Images';
    return 'Videos';
  });

  // State to preserve prompt and images per tab
  const [tabStates, setTabStates] = useState<{
    Videos: { prompt: string; hasImages: boolean; selectedModel: AIModel };
    Images: { prompt: string; hasImages: boolean; selectedModel: AIModel };
    Transcripts: { prompt: string; hasFile: boolean; selectedModel: AIModel };
  }>({
    Videos: { prompt: '', hasImages: false, selectedModel: 'Veo 3.1 Fast' },
    Images: { prompt: '', hasImages: false, selectedModel: 'Gemini 2.5' },
    Transcripts: { prompt: '', hasFile: false, selectedModel: 'Whisper' },
  });

  // Flag to prevent premature auto-cleanup of restored queued tiles
  const [cleanupReady, setCleanupReady] = useState(false);

  // Simple bypass state for forbidden words
  const [bypassForbiddenWords, setBypassForbiddenWords] = useState(false);

  // Toast notification state
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error'>('error');

  // Local uploading images state
  const [localUploadingImages, setLocalUploadingImages] = useState<Array<{id: string, prompt: string, uploadStartTime: number, progress?: number}>>([]);

  // Transcription complete modal state
  const [showTranscriptionCompleteModal, setShowTranscriptionCompleteModal] = useState(false);
  const [completedTranscription, setCompletedTranscription] = useState<Transcription | null>(null);
  const previousTranscriptionRef = useRef<Transcription | null>(null);
  const hasRestoredCompletedTranscription = useRef(false);

  // Create a ref for the video generation callback to avoid circular dependency
  const handleVideoGeneratedRef = useRef<((videoInfo: any) => void) | null>(null);

  // Initialize video generation hook first
  const videoGenerationHook = useVideoGeneration(); // Initialize without refetch
  const {
    prompt,
    selectedModel,
    isGenerating,
    isVideoGenerating,
    error,
    imagePreview,
    generateAudio,
    aspectRatio,
    imageAspectRatio,
    imageResolution,
    queuedVideos,
    queuedImages,
    uploadingVideos,
    uploadedImagesCount,
    videoImagePreviews,
    setVideoUploadedImages,
    setVideoImagePreviews,
    imageGenPreviews,
    setImageGenUploadedImages,
    setImageGenPreviews,
    setPrompt,
    setAspectRatio,
    setImageAspectRatio,
    setImageResolution,
    handleModelSelect,
    handleGenerateWithTile,
    cancelQueuedVideo,
    handleCancel,
    handleImageUpload,
    removeImage,
    removeImageAtIndex,
    toggleAudio,
    handleVideoGenerated,
    clearError,
    handleVideoUpload,
    isUploading,
    uploadError,
    clearUploadError,
    clearUploadSuccess,
    setIsVideoGenerating,
    setOriginalVideoId,
  } = videoGenerationHook;

  // Wrapper functions that pass current tab to image upload/removal functions
  const handleImageUploadWithTab = useCallback((file: File) => {
    handleImageUpload(file, pageValue as TabType);
  }, [handleImageUpload, pageValue]);

  const removeImageWithTab = useCallback(() => {
    removeImage(pageValue as TabType);
  }, [removeImage, pageValue]);

  const removeImageAtIndexWithTab = useCallback((index: number) => {
    removeImageAtIndex(index, pageValue as TabType);
  }, [removeImageAtIndex, pageValue]);

  // Update the ref with the actual callback
  handleVideoGeneratedRef.current = handleVideoGenerated;

  // Compute the current image preview based on active tab
  const getImagePreviewForTab = (): string | null => {
    if (pageValue === 'Videos') {
      return videoImagePreviews.length > 0 ? videoImagePreviews[0] : null;
    }
    if (pageValue === 'Images') {
      return imageGenPreviews.length > 0 ? imageGenPreviews[imageGenPreviews.length - 1] : null;
    }
    return null;
  };
  const currentImagePreview = getImagePreviewForTab();

  // React Query integration for videos
  const {
    videos: userVideos,
    pinnedVideos,
    error: videosError,
    refetch,
    isLoading,
    isLoadingMore,
    hasMore,
    totalUnfilteredCount,
    handleDownloadLastFrame,
    setVideos,
    setPinnedVideos,
    optimisticPinVideo,
    optimisticUnpinVideo,
    isAllowedToPin,
    fetchAllowedToPin,
    loadMore,
    predictivePrefetch: predictivePrefetchVideos
  } = useUserVideosAdapter({
    ...serverFilters,
    userEmail: userEmail,
    isAdmin,
    limit: requestLimit,
    enabled: galleryEnabled,
  });
  const { showLoading, hideLoading } = useLoading();

  // React Query integration for images
  const {
    images: userImages,
    error: imagesError,
    refetch: refetchImages,
    loadMore: loadMoreImages,
    isLoading: isLoadingImages,
    isLoadingMore: isLoadingMoreImages,
    hasMore: hasMoreImages,
    totalUnfilteredCount: totalUnfilteredCountImages,
    setImages,
    predictivePrefetch: predictivePrefetchImages
  } = useUserImagesAdapter({
    ...imageFilters,
    userEmail,
    limit: requestLimit,
    enabled: galleryEnabled,
  });

  const {
    transcription,
    fetchTranscription,
    isLoading: isTranscriptionLoading,
    saveTranscription,
    deleteTranscription,
    handleFileSelection,
    previewUrl,
    isTranscription,
    file,
    fileType,
    handleRemoveFile,
    transcriptions,
    isLoadingTranscriptions,
    hasMore: hasMoreTranscriptions,
    loadMoreTranscriptions,
    isLoadingMore: isLoadingMoreTranscriptions,
    totalUnfilteredCount: totalUnfilteredCountTranscriptions,
    addTranscription,
  } = useTranscription();
  // Unified notifications system
  // Initialize video expiry manager for automatic cache invalidation

  // Restore completed transcription modal from localStorage on mount
  useEffect(() => {
    // Only restore once, when transcriptions are loaded
    if (hasRestoredCompletedTranscription.current || transcriptions.length === 0) {
      return;
    }

    const savedCompletedTranscription = localStorage.getItem('completedTranscription');

    if (savedCompletedTranscription) {
      try {
        const parsedTranscription = JSON.parse(savedCompletedTranscription) as Transcription;

        // Find the transcription in the current list to ensure it still exists
        const transcription = transcriptions.find(t =>
          t.created_at === parsedTranscription.created_at &&
          t.user_email === parsedTranscription.user_email &&
          t.title === parsedTranscription.title
        );

        if (transcription) {
          setCompletedTranscription(transcription);
          setShowTranscriptionCompleteModal(true);
          hasRestoredCompletedTranscription.current = true;
        } else {
          // Clean up if transcription no longer exists
          localStorage.removeItem('completedTranscription');
        }
      } catch (error) {
        console.error('Error parsing saved completed transcription:', error);
        localStorage.removeItem('completedTranscription');
      }
    }
  }, [transcriptions]);

  // Watch for transcription completion
  useEffect(() => {
    // Only show modal if:
    // 1. We have a new transcription
    // 2. It's not loading
    // 3. We're in Whisper mode
    // 4. The transcription has changed (to prevent showing on page load)
    if (
      transcription &&
      !isTranscriptionLoading &&
      selectedModel === 'Whisper' &&
      previousTranscriptionRef.current !== transcription
    ) {
      setCompletedTranscription(transcription);
      setShowTranscriptionCompleteModal(true);
      previousTranscriptionRef.current = transcription;

      // Save to localStorage for persistence across reloads
      localStorage.setItem('completedTranscription', JSON.stringify(transcription));
    }
  }, [transcription, isTranscriptionLoading, selectedModel]);

  useEffect(() => {
    if (selectedModel === 'Whisper') {
      setPageValue("Transcripts");
      handleRemoveFile();
    } else if (IMAGE_MODELS.includes(selectedModel as any)) {
      setPageValue("Images");
      handleRemoveFile();
    } else {
      setPageValue("Videos");
      handleRemoveFile();
    }
  }, [selectedModel]);

  // Update tabStates when model changes (user manually selects a different model)
  useEffect(() => {
    setTabStates(prev => ({
      ...prev,
      [pageValue]: {
        ...prev[pageValue as keyof typeof prev],
        selectedModel,
      }
    }));
  }, [selectedModel, pageValue]);

  // Handler for tab change
  const handleTabChange = (_event: React.MouseEvent<HTMLElement>, newValue: string | null) => {
    if (newValue) {
      // Save current tab state before switching
      const currentTab = pageValue as 'Videos' | 'Images' | 'Transcripts';
      setTabStates(prev => ({
        ...prev,
        [currentTab]: {
          prompt,
          hasImages: uploadedImagesCount > 0 || !!imagePreview,
          hasFile: !!file,
          selectedModel,
        }
      }));

      setPageValue(newValue);

      // Update model selection based on tab
      if (newValue === 'Transcripts') {
        localStorage.setItem('selectedPage', 'Transcripts');
        
        // Restore Transcripts tab state
        const transcriptsState = tabStates.Transcripts;
        localStorage.setItem('selectedModel', transcriptsState.selectedModel);
        handleModelSelect(transcriptsState.selectedModel);
        setPrompt(transcriptsState.prompt);
        if (!transcriptsState.hasFile) {
          handleRemoveFile();
        }
        // Don't clear images when switching tabs
      } else if (newValue === 'Images') {
        localStorage.setItem('selectedPage', 'Images');
        
        // Restore Images tab state
        const imagesState = tabStates.Images;
        localStorage.setItem('selectedModel', imagesState.selectedModel);
        handleModelSelect(imagesState.selectedModel);
        setPrompt(imagesState.prompt);
        // Don't clear images when switching tabs
        // Clear file for Images tab
        handleRemoveFile();
      } else {
        localStorage.removeItem('selectedPage');
        
        // Restore Videos tab state
        const videosState = tabStates.Videos;
        localStorage.setItem('selectedModel', videosState.selectedModel);
        handleModelSelect(videosState.selectedModel);
        setPrompt(videosState.prompt);
        // Don't clear images when switching tabs
        // Clear file for Videos tab
        handleRemoveFile();
      }
    }
  };

  // Get models based on the selected page/tab
  const getFilteredModels = useCallback(() => {
    if (pageValue === 'Videos') {
      // Show video generation models
      return VIDEO_MODELS;
    } else if (pageValue === 'Images') {
      // Show image generation models
      return IMAGE_MODELS;
    } else if (pageValue === 'Transcripts') {
      // Show transcription models
      return TRANSCRIPTION_MODELS;
    }
    // Default to video models
    return VIDEO_MODELS;
  }, [pageValue]);

  // Manage loading state for all data fetching on this page
  // Suppress overlay when a shared ?video= param is present so the modal
  // can open immediately without being blocked by the loading overlay.
  useEffect(() => {
    if (isLoading && !isGenerating && !videoParam) {
      showLoading('Loading...');
    } else {
      hideLoading();
    }
  }, [isLoading, isGenerating, showLoading, hideLoading, videoParam]);

  // Handle all errors as toast and simplified forbidden words bypass
  useEffect(() => {
    if (error?.includes('forbidden words')) {
      // This is a forbidden words error - enable bypass and show toast
      setBypassForbiddenWords(true);
      setToastMessage(error);
      setToastOpen(true);
    } else if (error) {
      // Any other error - just show as toast
      setToastMessage(error);
      setToastOpen(true);
    } else if (!error) {
      // Error was cleared or generation was successful - reset bypass
      setBypassForbiddenWords(false);
    }
  }, [error]);

  // Wait for initial load before enabling auto-cleanup
  // This prevents restored queued tiles from being removed too quickly
  useEffect(() => {
    const timer = setTimeout(() => {
      setCleanupReady(true);
    }, 2000); // Give localStorage restoration and initial data fetch 2 seconds
    return () => clearTimeout(timer);
  }, []);

  // Auto-cleanup: Remove queued tiles when matching videos/images appear
  // Only runs after cleanupReady is true to prevent premature removal of restored tiles
  useEffect(() => {
    if (!cleanupReady || !userVideos || queuedVideos.length === 0) return;

    for (const video of userVideos) {
      if (video.prompt) {
        const matchingTile = queuedVideos.find(q => q.prompt === video.prompt);
        // Only cleanup if tile exists AND is completed (not still processing)
        if (matchingTile && matchingTile.status !== 'processing' && matchingTile.status !== 'queued') {
          handleVideoGenerated(video);
        }
      }
    }
  }, [cleanupReady, userVideos, queuedVideos, handleVideoGenerated]);

  useEffect(() => {
    if (!cleanupReady || !userImages || queuedImages.length === 0) return;

    for (const image of userImages) {
      if (image.prompt) {
        const matchingTile = queuedImages.find(q => q.prompt === image.prompt);
        // Only cleanup if tile exists AND is completed (not still processing)
        if (matchingTile && matchingTile.status !== 'processing' && matchingTile.status !== 'queued') {
          handleVideoGenerated(image);
        }
      }
    }
  }, [cleanupReady, userImages, queuedImages, handleVideoGenerated]);
  useEffect(() => {
    if (!cleanupReady || !userVideos || uploadingVideos.length === 0) return;

    for (const uploadingVideo of uploadingVideos) {
      const matchingVideo = userVideos.find(v =>
        v.prompt === uploadingVideo.prompt &&
        v.video_type === 'uploaded'
      );

      if (matchingVideo) {
        handleVideoGenerated(matchingVideo);
      }
    }
  }, [cleanupReady, userVideos, uploadingVideos, handleVideoGenerated]);

  const { subscribe, unsubscribe, pinVideoWebSocket, unpinVideoWebSocket } = useWebSocket();

  // Admin delete handlers using React Query mutations (with optimistic updates)
  const handleAdminDeleteVideo = useCallback(async (videoId: string) => {
    if (!isAdmin) return;

    // Find the video to get user email
    const video = userVideos.find(v => v.id === videoId) || pinnedVideos.find(v => v.id === videoId);
    const videoUserEmail = video?.user_email || userEmail || '';

    deleteVideoMutation.mutate({ videoId, userEmail: videoUserEmail });
  }, [isAdmin, userVideos, pinnedVideos, userEmail, deleteVideoMutation]);

  const handleAdminDeleteImage = useCallback(async (imageId: string) => {
    if (!isAdmin) return;
    deleteImageMutation.mutate(imageId);
  }, [isAdmin, deleteImageMutation]);

  // Helper functions for WebSocket message handling
  const updateVideosList = useCallback((videoId: string, status: string, feedback?: string) => {
    setVideos((prev: UserVideo[]) => prev.map((v: UserVideo) => {
      if (v.id === videoId) {
        return {
          ...v,
          status: status || v.status,
          feedback: feedback || v.feedback
        };
      }
      return v;
    }));
  }, [setVideos]);

  const updatePinnedVideosList = useCallback((videoId: string, status: string, feedback?: string) => {
    setPinnedVideos((prev: UserVideo[]) => prev.map((v: UserVideo) => {
      if (v.id === videoId) {
        return {
          ...v,
          status: status || v.status,
          feedback: feedback || v.feedback
        };
      }
      return v;
    }));
  }, [setPinnedVideos]);

  const updateImagesList = useCallback((imageId: string, status: string, feedback?: string, title?: string) => {
    setImages((prev: UserImage[]) => {
      const imageExists = prev.some((image: UserImage) => image.id === imageId);
      if (!imageExists) {
        return prev;
      }

      return prev.map((image: UserImage) => {
        if (image.id === imageId) {
          return {
            ...image,
            status: status || image.status,
            feedback: feedback || image.feedback,
            title: title || image.title,
            updated_at: new Date().toISOString()
          };
        }
        return image;
      });
    });
  }, [setImages]);

  // Simplified wrapper function with boolean bypass
  const handleGenerateWithForbiddenWordsTracking = useCallback(async () => {
    if (selectedModel === 'Whisper') {
      await fetchTranscription(file as File);
      return;
    }

    // Clear any existing errors before attempting generation
    if (error) {
      clearError();
    }

    // Pass the current tab to handleGenerateWithTile
    await handleGenerateWithTile(undefined, bypassForbiddenWords, pageValue as TabType);
  }, [selectedModel, fetchTranscription, file, handleGenerateWithTile, error, clearError, bypassForbiddenWords, pageValue]);

  const handleVideoUpdatedEvent = useCallback((parsedData: any) => {
    // Ignore admin optimistic updates - only respond to backend confirmed updates
    if (parsedData.data?.admin_optimistic === true || parsedData.data?.backend_confirmed === false) {
      return;
    }

    // Only update if this video belongs to the current user AND it's backend confirmed
    if (parsedData.data?.user_email === userEmail && parsedData.data?.backend_confirmed !== false) {
      const { video_id, status, feedback } = parsedData.data;
      updateVideosList(video_id, status, feedback);
      updatePinnedVideosList(video_id, status, feedback);
    }
  }, [userEmail, updateVideosList, updatePinnedVideosList]);

  const handleImageUpdatedEvent = useCallback((parsedData: any) => {
    // Ignore admin optimistic updates - only respond to backend confirmed updates
    if (parsedData.data?.admin_optimistic === true || parsedData.data?.backend_confirmed === false) {
      return;
    }

    // Only update if this image belongs to the current user AND it's backend confirmed
    if (parsedData.data?.user_email === userEmail && parsedData.data?.backend_confirmed !== false) {
      const { image_id, status, feedback, title } = parsedData.data;
      updateImagesList(image_id, status, feedback, title);
    }
  }, [userEmail, updateImagesList]);

  const handleVideoGeneratedEvent = useCallback((parsedData: any) => {
    const videoInfo = parsedData.data?.video_info || parsedData.video_info;
    if (parsedData.data?.user_email === userEmail && videoInfo) {
      const newVideo: UserVideo = {
        id: videoInfo.id,
        user_email: videoInfo.user_email,
        user_id: videoInfo.user_id,
        video_url: videoInfo.video_url || videoInfo.url || '',
        file_size: videoInfo.file_size || videoInfo.size || 0,
        title: videoInfo.title || 'Generated Video',
        video_type: videoInfo.video_type || 'generated',
        status: videoInfo.status || 'completed',
        feedback: videoInfo.feedback,
        prompt: videoInfo.prompt || '',
        model: videoInfo.model || '',
        duration: videoInfo.duration || 0,
        thumbnail_url: videoInfo.thumbnail_url || videoInfo.thumbnail || '',
        created_at: videoInfo.created_at || new Date().toISOString(),
        updated_at: videoInfo.updated_at || new Date().toISOString(),
        is_pinned: false,
        pinned_by: undefined,
        pinned_at: undefined,
        delete_request: false
      };

      // Only add if it doesn't already exist (prevent duplicates)
      setVideos((prev: UserVideo[]) => {
        if (prev.some(v => v.id === newVideo.id)) {
          return prev; // Video already exists, don't add duplicate
        }
        return [newVideo, ...prev];
      });

      // Immediately remove the processing tile to ensure smooth transition
      handleVideoGenerated(newVideo);
    }
  }, [userEmail, setVideos, handleVideoGenerated]);

  const handleImageGeneratedEvent = useCallback((parsedData: any) => {
    const imageInfo = parsedData.data?.image_info || parsedData.image_info;
    if (parsedData.data?.user_email === userEmail && imageInfo) {
      const newImage: UserImage = {
        id: imageInfo.id,
        user_email: imageInfo.user_email,
        user_id: imageInfo.user_id,
        image_url: imageInfo.image_url || imageInfo.url || '',
        file_name: imageInfo.file_name || imageInfo.title || 'Generated Image',
        file_size: imageInfo.file_size || imageInfo.size || 0,
        title: imageInfo.title || 'Generated Image',
        image_type: imageInfo.image_type || 'generated',
        status: imageInfo.status || 'completed',
        feedback: imageInfo.feedback,
        prompt: imageInfo.prompt || '',
        model: imageInfo.model || '',
        width: imageInfo.width || 1024,
        height: imageInfo.height || 1024,
        aspect_ratio: imageInfo.aspect_ratio || '1:1',
        format: imageInfo.format || 'png',
        created_at: imageInfo.created_at || new Date().toISOString(),
        updated_at: imageInfo.updated_at || new Date().toISOString(),
        delete_request: false
      };

      // Only add if it doesn't already exist (prevent duplicates)
      setImages((prev: UserImage[]) => {
        if (prev.some(img => img.id === newImage.id)) {
          return prev; // Image already exists, don't add duplicate
        }
        return [newImage, ...prev];
      });

      // Clear all uploading tiles when image is generated/uploaded
      setLocalUploadingImages([]);

      // Immediately remove the processing tile to ensure smooth transition
      handleVideoGenerated(newImage);
    }
  }, [userEmail, setImages, handleVideoGenerated]);

  // Subscribe to WebSocket messages for real-time updates
  useEffect(() => {
    const handleWebSocketMessage = (data: any) => {
      let parsedData;
      try {
        parsedData = typeof data === 'string' ? JSON.parse(data) : data;
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
        return;
      }
      if (parsedData.event === 'video_updated') {
        handleVideoUpdatedEvent(parsedData);
      } else if (parsedData.event === 'video_generated') {
        handleVideoGeneratedEvent(parsedData);
      } else if (parsedData.event === 'video_uploaded') {
        // Handle uploaded videos the same way as generated videos
        handleVideoGeneratedEvent(parsedData);
      } else if (parsedData.event === 'image_updated') {
        handleImageUpdatedEvent(parsedData);
      } else if (parsedData.event === 'image_generated' || parsedData.event === 'image_uploaded') {
        handleImageGeneratedEvent(parsedData);
      }
    };

    subscribe('user-dashboard', handleWebSocketMessage);

    return () => {
      unsubscribe('user-dashboard');
    };
  }, [subscribe, unsubscribe, handleVideoUpdatedEvent, handleVideoGeneratedEvent, handleImageUpdatedEvent, handleImageGeneratedEvent]);

  // Pin/unpin functions are now provided by useWebSocket context
  // WebSocket connection status is handled by the WebSocket context

  const getAcceptedFileTypes = () => {
    if (selectedModel === 'Whisper') {
      return ['video/', 'audio/'];
    }
    return ['image/'];
  };

  const handleDragDropFile = useCallback((file: File) => {
    if (selectedModel === 'Whisper') {
      handleFileSelection(file);
    } else {
      // For video/image models, use handleImageUpload with current tab
      handleImageUploadWithTab(file);
    }
  }, [selectedModel, handleFileSelection, handleImageUploadWithTab]);

  const { dragHandlers, isDragging } = useDragAndDrop({
    enabled: true,
    onFileSelect: handleDragDropFile,
    isUploading: isTranscriptionLoading,
    acceptedTypes: getAcceptedFileTypes(),
    onInvalidFile: () => {
      const fileType = selectedModel === 'Whisper' ? 'video or audio' : 'image';
      setToastMessage(`Invalid file type. Please upload ${fileType} files only.`);
      setToastOpen(true);
    }
  });

  const handleToastClose = useCallback((_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setToastOpen(false);
  }, []);

  // Helper to check if current model is a video model
  const isVideoModel = useCallback((model: string) => {
    return model === 'Veo 3.1' || model === 'Veo 3.1 Fast' ||
           model === 'Veo 3' || model === 'Veo 3 Fast' || 
           model === 'Veo 2';
  }, []);

  // Helper to handle Videos tab image attachment
  const handleVideosTabAttachment = useCallback((imageUrl: string) => {
    // Determine the effective model (may change if switching to Videos tab)
    let effectiveModel = selectedModel;
    if (pageValue !== 'Videos' && !isVideoModel(selectedModel)) {
      effectiveModel = 'Veo 3.1 Fast' as AIModel;
    }
    
    const maxImages = supportsMultipleImages(effectiveModel) ? 3 : 1;
    
    if (videoImagePreviews.length >= maxImages) {
      setToastMessage(`Videos tab allows maximum ${maxImages} image upload${maxImages > 1 ? 's' : ''}`);
      setToastSeverity('error');
      setToastOpen(true);
      return;
    }
    
    // Keep videoImagePreviews and videoUploadedImages in sync
    // Gallery images don't have a File object, so we add a null placeholder
    setVideoUploadedImages(prev => [...prev, null as any]);
    setVideoImagePreviews(prev => [...prev, imageUrl]);
    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
    
    if (pageValue !== 'Videos') {
      setPageValue('Videos');
      localStorage.removeItem('selectedPage');
      
      if (!isVideoModel(selectedModel)) {
        const newModel = 'Veo 3.1 Fast';
        localStorage.setItem('selectedModel', newModel);
        handleModelSelect(newModel);
      }
      
      handleRemoveFile();
    }
    
    setToastMessage('Image attached to Videos tab');
    setToastSeverity('success');
    setToastOpen(true);
  }, [videoImagePreviews, pageValue, selectedModel, isVideoModel, handleModelSelect, handleRemoveFile, setVideoUploadedImages]);

  // Helper to handle Images tab image attachment
  const handleImagesTabAttachment = useCallback((imageUrl: string) => {
    if (imageGenPreviews.length >= 3) {
      setToastMessage('Images tab allows maximum 3 images');
      setToastSeverity('error');
      setToastOpen(true);
      return;
    }
    
    // Keep imageGenPreviews and imageGenUploadedImages in sync
    // Gallery images don't have a File object, so we add a null placeholder
    setImageGenUploadedImages(prev => [...prev, null as any]);
    setImageGenPreviews(prev => [...prev, imageUrl]);
    
    if (pageValue !== 'Images') {
      setPageValue('Images');
      localStorage.removeItem('selectedPage');
      
      if (selectedModel !== 'Gemini 2.5' && selectedModel !== 'Gemini 3 Pro') {
        const newModel = 'Gemini 2.5';
        localStorage.setItem('selectedModel', newModel);
        handleModelSelect(newModel);
      }
      
      handleRemoveFile();
    }
    
    setToastMessage('Image attached to Images tab');
    setToastSeverity('success');
    setToastOpen(true);
  }, [imageGenPreviews, pageValue, selectedModel, handleModelSelect, handleRemoveFile, setImageGenUploadedImages]);

  // Handler to attach an image from the gallery to the input
  const handleAttachImageFromGallery = useCallback((image: UserImage, tab: 'Videos' | 'Images') => {
    try {
      const imageUrl = image.image_url;
      
      if (tab === 'Videos') {
        handleVideosTabAttachment(imageUrl);
      } else if (tab === 'Images') {
        handleImagesTabAttachment(imageUrl);
      }
    } catch (error) {
      console.error('Failed to attach image:', error);
      setToastMessage('Failed to attach image. Please try again.');
      setToastSeverity('error');
      setToastOpen(true);
    }
  }, [handleVideosTabAttachment, handleImagesTabAttachment]);

  // State to track if we're processing last frame attachment
  const [isProcessingLastFrame, setIsProcessingLastFrame] = useState(false);

  // Handler to attach last frame of a video to the Videos tab
  const handleAttachVideoLastFrame = useCallback(async (video: UserVideo) => {
    // Store original prompt to restore if switching tabs
    const originalPrompt = prompt;
    
    // Set processing state to disable generate button
    setIsProcessingLastFrame(true);
    
    // Animate dots in the processing message
    let dotCount = 0;
    const animateDots = setInterval(() => {
      dotCount = (dotCount + 1) % 4;
      const dots = '.'.repeat(dotCount || 1);
      setPrompt(`Processing last frame${dots}`);
    }, 500);
    
    try {
      // Show loading toast
      setToastMessage('Extracting last frame...');
      setToastSeverity('success');
      setToastOpen(true);

      // Get the last frame as base64
      const lastFrameBase64 = await getVideoLastFrame(video.video_url);
      
      if (!lastFrameBase64) {
        throw new Error('Failed to extract last frame');
      }

      // Convert base64 to data URL
      const dataUrl = `data:image/jpeg;base64,${lastFrameBase64}`;
      
      // Save the original video ID for extended video generation
      setOriginalVideoId(video.id);
      
      // For extended video feature, replace all existing images with only the last frame
      setVideoUploadedImages([null as any]);
      setVideoImagePreviews([dataUrl]);
      
      // Switch to Videos tab if not already there
      if (pageValue !== 'Videos') {
        setPageValue('Videos');
        localStorage.removeItem('selectedPage');
        
        if (!isVideoModel(selectedModel)) {
          const newModel = 'Veo 3.1 Fast';
          localStorage.setItem('selectedModel', newModel);
          handleModelSelect(newModel);
        }
        
        handleRemoveFile();
      }
      
      // Clear the processing message and stop animation
      clearInterval(animateDots);
      setPrompt('');
      setIsProcessingLastFrame(false);
      
      // Scroll to top to show the attached frame
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
      
      // Update toast message
      setToastMessage('Last frame attached to Videos tab');
      setToastSeverity('success');
      setToastOpen(true);
    } catch (error) {
      console.error('Failed to attach video last frame:', error);
      
      // Stop animation and restore original prompt on error
      clearInterval(animateDots);
      setPrompt(originalPrompt);
      setIsProcessingLastFrame(false);
      
      setToastMessage('Failed to extract last frame. Please try again.');
      setToastSeverity('error');
      setToastOpen(true);
    }
  }, [pageValue, selectedModel, isVideoModel, handleModelSelect, handleRemoveFile, setVideoUploadedImages, prompt, setPrompt]);

  return (
    <Box
      className={LAYOUT_CLASSES.container}
      {...dragHandlers}
    >
      {isDragging && (
        <div
          data-drag-overlay
          className="fixed inset-0 flex items-center justify-center bg-blue-100 bg-opacity-75 z-50"
        >
          <div className="text-blue-700 font-medium text-xl">
            {selectedModel === 'Whisper' 
              ? 'Drop your video or audio file anywhere'
              : 'Drop your image file anywhere'}
          </div>
        </div>
      )}
      <DashboardHeader isAdmin={isAdmin} userEmail={userEmail} />

      <Box 
        component="main" 
        className={LAYOUT_CLASSES.main}
        style={{
          filter: isDragging ? 'blur(8px)' : 'none',
          transition: 'filter 300ms',
        }}
      >

        <PicassoControls
          isTranscriptionLoading={isTranscriptionLoading}
          isProcessingLastFrame={isProcessingLastFrame}
          onSaveTranscription={(transcription: Transcription) => saveTranscription(transcription)}
          transcription={transcription}
          prompt={prompt}
          onPromptChange={setPrompt}
          selectedModel={selectedModel}
          onModelSelect={handleModelSelect}
          orderedModels={getFilteredModels()}
          onGenerate={handleGenerateWithForbiddenWordsTracking}
          isGenerating={isGenerating}
          imagePreview={currentImagePreview}
          onImageUpload={handleImageUploadWithTab}
          onRemoveImage={removeImageWithTab}
          onRemoveImageAtIndex={removeImageAtIndexWithTab}
          generateAudio={generateAudio}
          onToggleAudio={toggleAudio}
          aspectRatio={aspectRatio}
          onAspectRatioChange={setAspectRatio}
          imageAspectRatio={imageAspectRatio}
          onImageAspectRatioChange={setImageAspectRatio}
          imageResolution={imageResolution}
          onImageResolutionChange={setImageResolution}
          file={file}
          handleFileSelection={handleFileSelection}
          handleRemoveFile={handleRemoveFile}
          previewUrl={previewUrl}
          isTranscription={isTranscription}
          fileType={fileType}
          pageValue={pageValue}
          onPageChange={handleTabChange}
          uploadedImagesCount={uploadedImagesCount}
          allImagePreviews={(() => {
            if (pageValue === 'Videos') return videoImagePreviews;
            if (pageValue === 'Images') return imageGenPreviews;
            return [];
          })()}
        />

        <VideoGallery
          pageValue={pageValue}
          transcriptions={transcriptions}
          isLoadingTranscriptions={isLoadingTranscriptions}
          isLoadingMoreTranscriptions={isLoadingMoreTranscriptions}
          loadMoreTranscriptions={loadMoreTranscriptions}
          hasMoreTranscriptions={hasMoreTranscriptions}
          totalUnfilteredCountTranscriptions={totalUnfilteredCountTranscriptions}
          deleteTranscription={deleteTranscription}
          data-testid="video-gallery"
          isAdmin={isAdmin}
          userEmail={userEmail}
          pinVideoWebSocket={pinVideoWebSocket}
          unpinVideoWebSocket={unpinVideoWebSocket}
          pinnedVideos={pinnedVideos}
          userVideos={userVideos}
          userImages={userImages}
          queuedVideos={queuedVideos}
          queuedImages={queuedImages}
          uploadingVideos={uploadingVideos}
          error={videosError}
          imagesError={imagesError}
          onRetry={refetch}
          onRetryImages={refetchImages}
          isLoading={isLoading}
          isLoadingImages={isLoadingImages}
          isLoadingMoreImages={isLoadingMoreImages}
          isLoadingMore={pageValue === "Videos" ? isLoadingMore : isLoadingMoreTranscriptions}
          hasMore={hasMore}
          hasMoreImages={hasMoreImages}
          totalUnfilteredCount={totalUnfilteredCount}
          totalUnfilteredCountImages={totalUnfilteredCountImages}
          handleDownloadLastFrame={handleDownloadLastFrame}
          cancelQueuedVideo={cancelQueuedVideo}
          handleCancel={handleCancel}
          optimisticPinVideo={optimisticPinVideo}
          optimisticUnpinVideo={optimisticUnpinVideo}
          isAllowedToPin={isAllowedToPin}
          fetchAllowedToPin={fetchAllowedToPin}
          serverFilters={serverFilters}
          setServerFilters={setServerFilters}
          imageFilters={imageFilters}
          setImageFilters={setImageFilters}
          loadMore={loadMore}
          loadMoreImages={loadMoreImages}
          predictivePrefetchVideos={predictivePrefetchVideos}
          predictivePrefetchImages={predictivePrefetchImages}
          localUploadingImages={localUploadingImages}
          setLocalUploadingImages={setLocalUploadingImages}
          handleVideoUpload={handleVideoUpload}
          isUploading={isUploading}
          uploadError={uploadError}
          clearUploadError={clearUploadError}
          clearUploadSuccess={clearUploadSuccess}
          onDeleteVideo={isAdmin ? handleAdminDeleteVideo : undefined}
          onDeleteImage={isAdmin ? handleAdminDeleteImage : undefined}
          isGenerating={isVideoGenerating}
          setIsGenerating={setIsVideoGenerating}
          addTranscription={addTranscription}
          onAttachImage={handleAttachImageFromGallery}
          onAttachVideoLastFrame={handleAttachVideoLastFrame}
          onSharedVideoReady={() => setGalleryEnabled(true)}
        />
      </Box>

      <Snackbar
        open={toastOpen}
        autoHideDuration={2000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleToastClose} severity={toastSeverity} variant="filled">
          {toastMessage}
        </Alert>
      </Snackbar>

      {/* Transcription Complete Modal */}
      <TranscriptionCompleteModal
        isOpen={showTranscriptionCompleteModal}
        onClose={() => {
          setShowTranscriptionCompleteModal(false);
          localStorage.removeItem('completedTranscription');
        }}
        onDownload={() => {
          if (completedTranscription) {
            const blob = new Blob([completedTranscription.transcription], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${completedTranscription.title || 'transcription'}.txt`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
          }
          setShowTranscriptionCompleteModal(false);
          localStorage.removeItem('completedTranscription');
        }}
        onDelete={async () => {
          if (completedTranscription?.id) {
            try {
              await deleteTranscription(completedTranscription.id);
              setShowTranscriptionCompleteModal(false);
              setCompletedTranscription(null);
              localStorage.removeItem('completedTranscription');
            } catch (error) {
              console.error('Failed to delete transcription:', error);
            }
          }
        }}
        transcriptionTitle={completedTranscription?.title || 'Transcription'}
        transcriptionText={completedTranscription?.transcription || ''}
      />
    </Box>
  );
};

export default PicassoDashboard;

